import { memo, useState } from 'react';
import useMediaQuery from '@mui/material/useMediaQuery';
import { 
  TextField, 
  Drawer, 
  Box, 
  Button, 
} from '@mui/material';
import _isEmpty from 'lodash/isEmpty';
import _omit from 'lodash/omit';
// Local file
import ErrorWrapper from 'components/Common/ErrorWrapper/ErrorWrapper';
import useCustomSelector from 'hooks/useCustomSelector';
import useApp from 'hooks/useApp';
import useSuccess from 'hooks/useSuccess';
import useSixthFlows from 'hooks/useSixthFlows';
import { appSelectors } from 'features/app';
import { sixthFlowsSelectors } from 'features/sixthFlows';
import { checkEmptyString, needToUpdateField } from 'helpers';

const SixthFlowPromptsDrawer = () => {
  const mediaDesktop = useMediaQuery(theme => theme.breakpoints.up('md'));
  const { setSixthFlowPromptsDrawer } = useApp();
  const { setSuccess } = useSuccess();
  const { getSixthFlow, updateSixthFlow } = useSixthFlows();
  const { open, edit, id, title, text } = useCustomSelector(appSelectors.getSixthFlowPromptsDrawerData);
  const processing = useCustomSelector(sixthFlowsSelectors.sixthFlowIsFetching);
  const [promptTitle, setPromptTitle] = useState({ value: '', error: '' });
  const [promptText, setPromptText] = useState({ value: '', error: '' });

  const handleClose = () => setSixthFlowPromptsDrawer({ open: false, edit: false, id: '', title: '', text: '' });

  const handleSave = ({ deletePrompt = false }) => {
    let hasError = false;

    if (!checkEmptyString(promptTitle.value)) {
      setPromptTitle(prev => ({ ...prev, error: `Title can't be empty` }) );
      hasError = true;
    }
    if (!checkEmptyString(promptText.value)) {
      setPromptText(prev => ({ ...prev, error: `Text can't be empty` }) );
      hasError = true;
    }

    if (hasError) return;

    const sixth_flow = {
      sixth_prompts_attributes: [{ 
        ...(edit && { id }), 
        ...(needToUpdateField(title, promptTitle.value) && {  title: promptTitle.value }),
        ...(needToUpdateField(text, promptText.value) && { text: promptText.value }),
        ...(deletePrompt && { _destroy: true })
      }]
    };
    if (!_isEmpty(_omit(sixth_flow.sixth_prompts_attributes[0], ['id']))) {
      updateSixthFlow(sixth_flow)
      .then(({ error }) => {
        if (!error) {
          getSixthFlow()
          .then(({ error }) => {
            if (!error) {
              setSuccess(deletePrompt ? 'Prompts successfully removed' : 'Prompts successfully updated');
              handleClose();
            }
          })
        }
      }) 
    } else {
      handleClose();
    }
  } 

  const handleEntering = () => {
   setPromptTitle({ value: title, error: '' });
   setPromptText({ value: text, error: '' });
  };

  const handleExiting = () => {
    setPromptTitle({ value: '', error: '' });
    setPromptText({ value: '', error: '' });
  };

  return (
    <Drawer
      anchor={!mediaDesktop ? 'bottom' : 'right'}
      open={open}
      SlideProps={{ onExited: handleExiting, onEntering: handleEntering }}
      onClose={handleClose}
      ModalProps={{ sx: { zIndex: 1300 } }}
      PaperProps={{
        sx: {
          height: '100%',
          width: { xs: '100%', md: '600px'},
          backgroundColor: 'background.default'
        }
      }}
    >
      <Box>
        <Box sx={{ display: 'flex', p: '8px 8px 0' }}>
          <Button color='inherit' sx={{ fontWeight: 700 }} onClick={handleClose}>Close</Button>
          <div style={{ flexGrow: 1 }}/>
          {edit && <Button color='error' disabled={processing} onClick={() => handleSave({deletePrompt: true})} sx={{ fontWeight: 700 }}>Delete</Button>}
        </Box>
        <Box sx={{ fontSize: '2rem', fontWeight: 'bold', textAlign: 'center'}}>
          {!edit ? 'Create' : 'Edit'} Prompt
        </Box>
      </Box>
      <Box sx={{ flexGrow: 1, p: { xs: 2, md: '16px 100px 32px' } }}>
        <TextField
          fullWidth
          margin='normal'
          label='Title'
          helperText={promptTitle.error}
          error={!!promptTitle.error}
          value={promptTitle.value}
          onChange={({ target: { value } }) => setPromptTitle({ value, error: '' })}
        />
        <TextField
          fullWidth
          margin='normal'
          label='Text'
          multiline
          minRows={5}
          maxRows={8}
          helperText={promptText.error}
          error={!!promptText.error}
          value={promptText.value}
          onChange={({ target: { value } }) => setPromptText({ value, error: '' })}
        />   
        <Button
          variant='contained'
          sx={{ display: 'flex', mx: 'auto', px: 5, mt: 3 }}
          onClick={handleSave}
        >
          Save
        </Button>      
      </Box>
    </Drawer>
  );
};

const WrappedComponent = props => {
  return (
    <ErrorWrapper>
      <SixthFlowPromptsDrawer {...props} />
    </ErrorWrapper>
  );
};

export default memo(WrappedComponent);